import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { User, Buildings, PaperPlaneTilt, LockKey, FlowArrow, ArrowSquareOut } from "@phosphor-icons/react";
import { api } from "../lib/api";

const PAGES = [
  { label: "Dashboard", to: "/" }, { label: "Contacts", to: "/contacts" }, { label: "Companies", to: "/companies" },
  { label: "Lists", to: "/lists" }, { label: "Segments", to: "/segments" }, { label: "Templates", to: "/templates" },
  { label: "Campaigns", to: "/campaigns" }, { label: "Sequences", to: "/sequences" }, { label: "Publications", to: "/publications" },
  { label: "Analytics", to: "/analytics" }, { label: "Sender Health", to: "/health" }, { label: "Suppressions", to: "/suppressions" },
  { label: "Settings", to: "/settings" }, { label: "Guide", to: "/guide" },
];

const STATUS_COLORS = { sent: "text-emerald-600", draft: "text-zinc-400", scheduled: "text-indigo-600", sending: "text-amber-600", active: "text-emerald-600", paused: "text-amber-600" };

export function CommandPalette({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => { if (!open) { setQ(""); setRes(null); } }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    setBusy(true);
    const t = setTimeout(() => {
      api.globalSearch(q.trim()).then(setRes).catch(() => {}).finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); nav(path); };
  const pages = q.trim().length >= 2 ? PAGES.filter(p => p.label.toLowerCase().includes(q.trim().toLowerCase())) : [];
  const hasResults = res && (res.contacts.length || res.companies.length || res.campaigns.length || res.publications.length || res.sequences.length || pages.length);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 max-w-xl top-[20%] translate-y-0" data-testid="command-palette">
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-zinc-400">
          <CommandInput data-testid="palette-input" placeholder="Search contacts, companies, campaigns…" value={q} onValueChange={setQ}/>
          <CommandList className="max-h-[420px]">
            {q.trim().length < 2 && <div className="py-10 text-center text-sm text-zinc-400">Type at least 2 characters…</div>}
            {q.trim().length >= 2 && !busy && !hasResults && <CommandEmpty>No matches for “{q}”</CommandEmpty>}

            {res?.contacts.length > 0 && (
              <CommandGroup heading="Contacts">
                {res.contacts.map(c => (
                  <CommandItem key={c.id} value={`contact-${c.id}`} onSelect={() => go(`/contacts/${c.id}`)} data-testid={`palette-contact-${c.id}`} className="gap-2.5">
                    <User size={15} className="text-zinc-400 shrink-0"/>
                    <span className="font-medium">{`${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}</span>
                    <span className="text-xs text-zinc-400 truncate">{c.email}{c.company ? ` · ${c.company}` : ""}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {res?.companies.length > 0 && (
              <CommandGroup heading="Companies">
                {res.companies.map(c => (
                  <CommandItem key={c.id} value={`company-${c.id}`} onSelect={() => go(`/contacts?company=${encodeURIComponent(c.name)}`)} className="gap-2.5">
                    <Buildings size={15} className="text-zinc-400 shrink-0"/>
                    <span className="font-medium">{c.name}</span>
                    {c.company_type && <span className="text-xs text-zinc-400">{c.company_type}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {res?.campaigns.length > 0 && (
              <CommandGroup heading="Campaigns">
                {res.campaigns.map(c => (
                  <CommandItem key={c.id} value={`campaign-${c.id}`} onSelect={() => go(`/campaigns/${c.id}`)} className="gap-2.5">
                    <PaperPlaneTilt size={15} className="text-zinc-400 shrink-0"/>
                    <span className="font-medium truncate">{c.name}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${STATUS_COLORS[c.status] || "text-zinc-400"}`}>{c.status}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {res?.publications.length > 0 && (
              <CommandGroup heading="Publications">
                {res.publications.map(p => (
                  <CommandItem key={p.id} value={`pub-${p.id}`} onSelect={() => go("/publications")} className="gap-2.5">
                    <LockKey size={15} className="text-zinc-400 shrink-0"/>
                    <span className="font-medium truncate">{p.title}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {res?.sequences.length > 0 && (
              <CommandGroup heading="Sequences">
                {res.sequences.map(s => (
                  <CommandItem key={s.id} value={`seq-${s.id}`} onSelect={() => go("/sequences")} className="gap-2.5">
                    <FlowArrow size={15} className="text-zinc-400 shrink-0"/>
                    <span className="font-medium truncate">{s.name}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${STATUS_COLORS[s.status] || "text-zinc-400"}`}>{s.status}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {pages.length > 0 && (
              <CommandGroup heading="Go to page">
                {pages.map(p => (
                  <CommandItem key={p.to} value={`page-${p.to}`} onSelect={() => go(p.to)} className="gap-2.5">
                    <ArrowSquareOut size={15} className="text-zinc-400 shrink-0"/>
                    <span>{p.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          <div className="border-t border-zinc-100 px-3 py-2 text-[10px] text-zinc-400 flex gap-3">
            <span>↑↓ navigate</span><span>↵ open</span><span>esc close</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
